<template>
    <Dialog
        :value="showDialog"
        :header-text="title"
        header_classes="black white--text"
        persistent
        @close_modal="close_modal"
    >
        <v-container v-if="form_data">
            <v-row no-gutters>
                <v-col cols="12">
                    <v-text-field
                        v-model="form_data.title"
                        :error-messages="title_error"
                        :label="$t('Title')"
                        outlined
                    />
                </v-col>
                <v-col cols="12">
                    <v-select
                        :value="form_data.type"
                        :items="show_field_types"
                        item-text="text"
                        item-value="type"
                        :label="$t('Field type')"
                        :disabled="!!form_data.id"
                        outlined
                        :error-messages="form_data.type_error"
                        @change="onTypeChange"
                    />
                </v-col>

                <v-col v-if="form_data.id && relatedType === 'task'" cols="12">
                    <v-text-field
                        v-model="form_data.name"
                        :error-messages="name_error"
                        :label="$t('Field name')"
                        :disabled="!!form_data.id"
                        outlined
                    />
                </v-col>

                <v-col v-if="relatedType === 'client'" cols="12">
                    <v-text-field
                        v-model="form_data.group_name"
                        :error-messages="form_data.group_name_error"
                        :label="$t('Client section')"
                        outlined
                    ></v-text-field>
                </v-col>
                <v-col v-if="form_data.type === 'choice_single'
                            || form_data.type === 'choice_multi'
                            || form_data.type === 'project_status'
                            || form_data.type === 'client_status'" cols="12">
                    <v-textarea
                        ref="choices_ctrl"
                        :label="$t('Choices')"
                        :value="choicesAsString"
                        :hint="$t('Type each choice on a separate line')"
                        :persistent-hint="true"
                        outlined
                        @change="onChoicesChange"
                    />
                </v-col>

                <v-col cols="12">
                    <v-checkbox
                        :input-value="form_data.required"
                        :true-value="1"
                        :false-value="0"
                        color="primary"
                        hide-details
                        @change="form_data.required = $event"
                    >
                        <div slot="label" class="subheading">
                            {{ $t("Required") }}
                        </div>
                    </v-checkbox>
                    <v-radio-group
                        v-if="form_data.required && relatedType === 'task'"
                        :value="form_data._data.required_type"
                        :error-messages="form_data._data && form_data._data.required_type_error"
                        class="mt-2"
                        row
                        @change="setRequiredType"
                    >
                        <v-row no-gutters>
                            <v-col cols="12" class="mb-2">
                                <v-radio
                                    :label="$t('For all topics')"
                                    :value="'all'"
                                    color="primary"
                                />
                            </v-col>
                            <v-col cols="12">
                                <v-radio
                                    :label="$t('For specific topics')"
                                    :value="'selected'"
                                    color="primary"
                                />
                            </v-col>
                        </v-row>
                    </v-radio-group>
                    <div v-if="relatedType === 'task'" class="mt-2">
                        <dropdown
                            v-if="form_data.required && form_data._data.required_type === 'selected'"
                            :items="topicStore.getAllTopics()"
                            :selected="form_data._data && form_data._data.required_ids ? topicStore.getAllTopics().filter(x => form_data._data.required_ids.includes(x.id)) : []"
                            name-key="name"
                            value-key="id"
                            show-selected-first
                            :error-messages="form_data._data && form_data._data.required_ids_error"
                            multi
                            deselect
                            :label="$t('Topics')"
                            @input="setRequiredIds"
                        />
                    </div>
                </v-col>

                <v-col v-if="show_monitoring" cols="12">
                    <v-checkbox :input-value="form_data.is_monitor"
                                :true-value="1"
                                :false-value="0"
                                color="primary"
                                @change="onIsMonitorChange">
                        <div slot="label" class="subheading">
                            {{ $t("Monitoring") }}
                        </div>
                    </v-checkbox>
                </v-col>

                <v-col v-if="relatedType === 'task' && isBillingEnabled" class="pb-8" cols="12">
                    <BillableButton
                        :value="form_data.billable"
                        :show-label="false"
                        :label="form_data.billable ? $t('Billable') : $t('Non-billable')"
                        @change="form_data.billable = ($event ? 1 : 0)"
                    />
                </v-col>
            </v-row>
        </v-container>

        <template v-if="form_data" slot="footer">
            <v-btn
                v-if="form_data.id"
                text
                color="error"
                @click="onDeleteClick"
            >
                {{ $t("Delete") }}
            </v-btn>
            <span style="color:white">{{ form_data.id }} {{ form_data.name }}</span>
            <v-spacer/>
            <v-btn text @click="close_modal">
                {{ $t("Close") }}
            </v-btn>
            <v-btn class="primary inverted--text" @click="onSaveClick">
                {{ $t("OK") }}
            </v-btn>
        </template>
    </Dialog>
</template>

<script>
import Vue from "vue";
import {computed, ref} from "@vue/composition-api";
import Dialog from "@/components/Dialog.vue";
import {$t} from "@/i18n";
import CustomFieldsModule from "@/store/modules/custom_fields";
import AccountModule from "@/store/modules/account";
import AppModule from "@/store/modules/app";
import DialogModule from "@/store/modules/dialog";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import BillableButton from "@/views/Task/TimeEntries/BillableButton.vue";
import {useTopicStore} from "../../../store/modules/topic";


export default {
    name: "CustomFieldEditDialog",
    components: {
        BillableButton,
        Dialog
    },
    props: {
        showDialog: {
            type: Boolean,
            required: true
        },
        customFieldId: {
            type: Number,
            required: true
        },
        relatedType: {
            type: String,
            required: true
        },
        accountId: {
            type: Number,
            required: true
        }
    },
    setup(props, ctx) {
        const {field_types, fetchCustomField, deleteCustomField, saveCustomField, fetchCustomFields} = CustomFieldsModule();
        const topicStore = useTopicStore()
        const {getAccount, isFeatureEnabled} = AccountModule();
        const {
            custom_fields,
            clearValidationErrors,
            handleErrorResponse,
            syncToClassifiersCache,
            removeFromClassifiersCache
        } = AppModule();
        const {setConfirmOptions, openConfirmDialog} = DialogModule();

        const form_data = ref(null);
        const loading = ref(false);

        const title = computed(() => {
            if (props.relatedType === "task") {
                return props.customFieldId ? $t("Edit task custom field") : $t("Add task custom field");
            } else if (props.relatedType === "client") {
                return props.customFieldId ? $t("Edit client custom field") : $t("Add client custom field");
            }
        });
        const show_field_types = computed(() => {
            if (form_data.value == null) return [];
            let types = field_types.value.filter(x => {
                if (form_data.value.type === x.type) {
                    return true
                }
                // allow to create any field types
                /*
                if (x.feature) {
                    if (!isFeatureEnabled(props.accountId, x.feature, false)) {
                        return false
                    }
                }
                */
                return x.related_type == null || x.related_type.indexOf(props.relatedType) >= 0 || x.type === form_data.value.type
            });

            types = types.map(x => {
                x.text = $t(x.text)

                return x
            })

            return types
        });
        const show_monitoring = computed(() => {
            if (form_data.value == null) return false;
            let account = getAccount(props.accountId);
            if (account.value && isFeatureEnabled(account.value.id, "monitoring", false)) {
                if (form_data.value.related_type === "task" && form_data.value.type === "number") return true;
            }
            return false;
        });
        const title_error = computed(function () {
            if (form_data.value == null) return null;
            if (form_data.value.title_error) return form_data.value.title_error;
            return null;
        });
        const name_error = computed(function () {
            if (form_data.value.name_error) return form_data.value.name_error;
            return null;
        });
        const choicesAsString = computed(() => {
            if (form_data.value == null) return [];
            let ret = [];
            if (form_data.value._data && form_data.value._data.choices) {
                for (let c of Array.from(form_data.value._data.choices)) {
                    if (c.code === c.name) {
                        ret.push(c.code);
                    } else {
                        ret.push("{0}: {1}".format(c.code, c.name));
                    }
                }
            }
            return ret.join("\n");
        });

        const loadData = () => {
            if (props.customFieldId > 0) {
                fetchCustomField({account_id: props.accountId, custom_field_id: props.customFieldId})
                    .then(resp => {
                        form_data.value = prepareFormData(resp);
                    })
                    .catch(ex => errorDialog.showError(ex));
            } else {
                form_data.value = prepareFormData(newField());
            }
        };
        const newField = () => {
            return {
                id: 0,
                related_type: props.relatedType,
                account_id: props.accountId,
                required: 0,
                is_visible: 1,
                is_listed: 0,
                is_editable: 1,
                is_deleted: 1,
                is_sortable: 1,
                is_monitor: 0,
                billable: 1,
            };
        };
        const prepareFormData = function (data) {
            data._data = Vue.prepare_form_data(data._data === undefined || data._data == null ? {} : data._data, {
                "choices": "list",
                "required_type": "choice_single",
                "required_ids": "list"
            });

            if (!['all', 'selected'].includes(data._data.required_type)) {
                data._data.required_type = 'all'
            }

            return Vue.prepare_form_data(data, {
                "title": "text",
                "type": "choice_single",
                "group_name": "text"
            });
        };
        const onTypeChange = val => {
            form_data.value.type = val;
            if (val === "choice_single") {
                if (!form_data.value._data["choices"]) {
                    const choice_format = $t("Choice {0}");
                    form_data.value._data["choices"] = [
                        {code: choice_format.format(1), name: choice_format.format(1)},
                        {code: choice_format.format(2), name: choice_format.format(2)},
                        {code: choice_format.format(3), name: choice_format.format(3)}
                    ];
                }
            }
        };
        const onChoicesChange = val => {
            form_data.value._data["choices"] = parseChoices(val);
        };
        const parseChoices = choices_str => {
            const ret = [];
            choices_str.split("\n").forEach(s => {
                let n;
                const arr = s.split(":");
                if (arr.length >= 2) {
                    const c = arr[0].trim();
                    n = arr[1].trim();

                    if (c.length > 0 && n.length > 0) {
                        ret.push({code: c, name: n});
                        return;
                    }
                    if (c.length > 0) {
                        ret.push({code: c, name: c});
                        return;
                    }
                    if (n.length > 0) {
                        ret.push({code: n, name: n});
                        return;
                    }
                }

                if (arr.length >= 1) {
                    n = arr[0].trim();
                    if (n.length > 0) {
                        return ret.push({code: n, name: n});
                    }
                }
            });

            return ret;
        };
        const onIsMonitorChange = val => {
            form_data.value.is_monitor = val;
        };
        const onDeleteClick = () => {
            setConfirmOptions({
                title: $t("Notice!"),
                text: $t("Are you sure that you want to delete this custom field?"),
                confirm_text: $t("Confirm"),
                cancel_text: $t("Cancel"),
                action: onDeleteConfirm
            });
            openConfirmDialog();
        };
        const onDeleteConfirm = async () => {
            try {
                await deleteCustomField(props.accountId, form_data.value.id)
                removeFromClassifiersCache("custom_fields", form_data.value.id, form_data.value.account_id);
                close_modal()
            } catch (ex) {
                setConfirmOptions({
                    title: $t("Warning!"),
                    text: $t("This custom field is in use. Do you wish to archive it?"),
                    confirm_text: $t("Archive"),
                    cancel_text: $t("Cancel"),
                    action: async () => {
                        await deleteCustomField(props.accountId, form_data.value.id, 0).catch(ex => errorDialog.showError(ex));
                        removeFromClassifiersCache("custom_fields", form_data.value.id, form_data.value.account_id);
                        close_modal()
                    }
                });
                openConfirmDialog();
            }
        };

        const onSaveClick = () => {
            loading.value = true;
            clearValidationErrors(form_data.value);

            let data = {};
            let has_data = false;
            if (form_data.value.type === "choice_single" || form_data.value.type === "choice_multi" || form_data.value.type === "project_status" || form_data.value.type === "client_status") {
                data.choices = form_data.value._data.choices;
                has_data = true;
            }
            if (form_data.value.type === "number" || form_data.value.type === "integer") {
                if (form_data.value._data.min_value !== undefined) {
                    data.min_value = form_data.value._data.min_value;
                    has_data = true;
                }
            }

            if (form_data.value.required) {
                data.required_ids = form_data.value._data.required_ids
                data.required_type = form_data.value._data.required_type
                has_data = true
            }

            let custom_field = {
                id: form_data.value.id,
                related_type: form_data.value.related_type,
                account_id: form_data.value.account_id,
                title: form_data.value.title,
                name: form_data.value.name,
                type: form_data.value.type,
                group_name: form_data.value.group_name,
                _data: has_data ? data : null,
                required: form_data.value.required === undefined ? 0 : form_data.value.required,
                is_visible: form_data.value.is_visible === undefined ? 0 : form_data.value.is_visible,
                billable: form_data.value.billable,
                is_monitor: form_data.value.is_monitor,
                is_deleted: 0,
            };

            saveCustomField(custom_field, props.relatedType)
                .then(resp => {
                    fetchCustomFields(props.accountId, 'task', 0, true)
                    syncToClassifiersCache("custom_fields", resp, form_data.value.account_id);
                    close_modal();
                    ctx.emit('save')
                    loading.value = false;
                })
                .catch(ex => {
                    loading.value = false;
                    handleErrorResponse(form_data.value, ex);
                });
        };
        const close_modal = () => {
            ctx.emit("close");
        };

        const setRequiredIds = (e) => {
            form_data.value._data.required_ids_error = null
            form_data.value._data.required_ids = e.map(x => x.id)
        }

        const setRequiredType = (e) => {
            form_data.value._data.required_type_error = null
            form_data.value._data.required_type = e
        }

        const isBillingEnabled = computed(() => {
            return isFeatureEnabled(props.accountId, 'billing', false) && (form_data.value.related_type === "task" && (form_data.value.type === "number" || form_data.value.type === "price"))
        })

        loadData();

        return {
            title,
            show_monitoring,
            show_field_types,
            title_error,
            name_error,
            choicesAsString,
            form_data,
            onTypeChange,
            onChoicesChange,
            onIsMonitorChange,
            onDeleteClick,
            onSaveClick,
            close_modal,
            topicStore,
            setRequiredIds,
            setRequiredType,
            isBillingEnabled
        };
    }
};
</script>
